import { CompanyTag } from "@/components/domain/CompanyTag";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Pencil, Plus, Search } from "lucide-react";
import { type InvoiceSummaryDto, type Paginated } from "@hisense/shared";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import { useCustomers, useInvoices } from "@/api/hooks";
import { PageBody } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { SearchableFilter } from "@/components/ui/SearchableFilter";
import { Stack } from "@/components/ui/Stack";
import { Td, Th, Tr } from "@/components/ui/Table";
import {
  EmptyTableRow,
  ListTableFrame,
  LIST_PAGE_BODY,
} from "@/components/ui/ListTable";
import { useClampListPage, useListQueryState, useParamInput } from "@/lib/listQuery";
import { ExportButton } from "@/components/domain/ExportButton";
import { formatMoney, majorUnits } from "@/domain/money";
import { formatDate } from "@/domain/datetime";
import { apiFetch } from "@/lib/api";
export function InvoicesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const list = useListQueryState();
  const respByInput = useParamInput(list, "respBy");
  const customers = useCustomers({});
  const full = list.searchParams.get("view") === "full";
  const archived = list.searchParams.get("archived") === "true";
  const context = list.searchParams.size ? `?${list.searchParams}` : "";
  const from = list.searchParams.get("from") ?? "";
  const to = list.searchParams.get("to") ?? "";
  const params = {
    q: list.q.trim() || undefined,
    page: list.page,
    pageSize: list.pageSize,
    customerId: list.searchParams.get("customerId") || undefined,
    respBy: list.searchParams.get("respBy") || undefined,
    from: from ? new Date(from + "T00:00:00Z").getTime() : undefined,
    to: to ? new Date(to + "T23:59:59Z").getTime() : undefined,
    sort: list.searchParams.get("sort") || "-date",
    archived: archived ? ("true" as const) : ("false" as const),
  };
  const result = useInvoices(params);
  useClampListPage(result.data?.total, list.clampPage);
  const rows = result.data?.data ?? [];
  const canCreate = Boolean(user && can("records:create"));
  const canEdit = Boolean(user && can("records:update"));
  return (
    <PageBody className={LIST_PAGE_BODY}>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Input
          aria-label={t("common.search")}
          {...list.searchInput}
          leading={<Search />}
          placeholder={t("common.search")}
          className="w-full max-w-xs"
        />
        <Select
          aria-label={t("invoiceWorkspace.lifecycle")}
          value={archived ? "true" : "false"}
          onChange={(e) =>
            list.updateParam(
              "archived",
              e.target.value === "true" ? "true" : null,
            )
          }
        >
          <option value="false">{t("invoiceWorkspace.active")}</option>
          <option value="true">{t("invoiceWorkspace.archived")}</option>
        </Select>
        <div className="ml-auto">
          <ExportButton<InvoiceSummaryDto>
            name="invoices"
            title={t("nav.invoices")}
            rows={rows}
            total={result.data?.total}
            disabled={result.isFetching}
            errorKey="invoices.errorLoad"
            fetchPage={(page, pageSize, signal) =>
              apiFetch<Paginated<InvoiceSummaryDto>>("/invoices", {
                query: { ...params, page, pageSize },
                signal,
              })
            }
            columns={[
              { header: t("invoices.invoiceNo"), value: (r) => r.invNo },
              { header: t("invoices.piNo"), value: (r) => r.piNo },
              { header: t("common.date"), value: (r) => formatDate(r.date) },
              { header: t("common.customer"), value: (r) => r.billToName },
              { header: t("pi.poNo"), value: (r) => r.customerPoNo },
              { header: t("pi.notifyPartyCustomer"), value: (r) => r.notifyPartyName },
              { header: t("pi.consigneeCustomer"), value: (r) => r.consigneeName },
              { header: t("common.currency"), value: (r) => r.currency },
              { header: t("pi.total"), value: (r) => majorUnits(r.totalAmount) },
              { header: t("common.items"), value: (r) => r.itemCount },
            ]}
          />
        </div>
        {canCreate && (
          <Button size="sm" onClick={() => navigate(`/invoices/new${context}`)}>
            <Plus className="size-4" />
            {t("invoices.newInvoice")}
          </Button>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <SearchableFilter
          label={t("common.customer")}
          allLabel={t("common.all")}
          searchPlaceholder={t("common.search")}
          value={params.customerId ?? ""}
          isLoading={customers.isLoading}
          error={customers.error ? t("customers.errorLoad") : null}
          onRetry={() => void customers.refetch()}
          options={(customers.data?.data ?? []).map((c) => ({
            value: c.id,
            label: c.companyName,
          }))}
          onChange={(v) => list.updateParam("customerId", v || null)}
        />
        <Input
          aria-label={t("pi.respBy")}
          placeholder={t("pi.respBy")}
          className="w-40"
          {...respByInput}
        />
        <Input
          type="date"
          aria-label={t("invoiceWorkspace.from")}
          value={from}
          onChange={(e) => list.updateParam("from", e.target.value || null)}
        />
        <Input
          type="date"
          aria-label={t("invoiceWorkspace.to")}
          value={to}
          onChange={(e) => list.updateParam("to", e.target.value || null)}
        />
        <Select
          aria-label={t("piWorkspace.sort")}
          value={params.sort}
          onChange={(e) => list.updateParam("sort", e.target.value)}
        >
          <option value="-date">{t("piWorkspace.newest")}</option>
          <option value="date">{t("piWorkspace.oldest")}</option>
          <option value="invNo">{t("invoices.invoiceNo")} ↑</option>
          <option value="-invNo">{t("invoices.invoiceNo")} ↓</option>
          <option value="total">{t("pi.total")} ↑</option>
          <option value="-total">{t("pi.total")} ↓</option>
        </Select>
        <Select
          aria-label={t("piWorkspace.columns")}
          value={full ? "full" : "finance"}
          onChange={(e) => list.updateParam("view", e.target.value)}
        >
          <option value="finance">{t("piWorkspace.financeView")}</option>
          <option value="full">{t("piWorkspace.fullView")}</option>
        </Select>
      </div>
      <ListTableFrame
        isLoading={result.isLoading}
        isFetching={result.isFetching}
        error={result.error}
        errorMessage={t("invoices.errorLoad")}
        onRetry={() => void result.refetch()}
        page={result.data?.page ?? list.page}
        pageSize={result.data?.pageSize ?? list.pageSize}
        total={result.data?.total ?? 0}
        onPageChange={list.updatePage}
        onPageSizeChange={list.updatePageSize}
      >
        <thead>
          <tr>
            <Th>#</Th>
            <Th pin>{t("invoices.invoiceNo")}</Th>
            <Th>{t("common.date")}</Th>
            <Th>{t("pi.billToContact")}</Th>
            <Th align="right">{t("pi.total")}</Th>
            <Th>{t("common.fromPi")}</Th>
            <Th>{t("pi.poNo")}</Th>
            <Th align="right">{t("common.items")}</Th>
            {full && (
              <>
                <Th>{t("pi.notifyParty")}</Th>
                <Th>{t("pi.consignee")}</Th>
                <Th>{t("pi.paymentCompany")}</Th>
                <Th>{t("common.created")}</Th>
              </>
            )}
            <Th pinRight>{t("common.actions")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <EmptyTableRow colSpan={full ? 13 : 9}>
              {t("common.noMatches")}
            </EmptyTableRow>
          )}
          {rows.map((r, i) => (
            <Tr key={r.id} interactive>
              <Td className="font-mono text-xs text-ink-faint">
                {(list.page - 1) * list.pageSize + i + 1}
              </Td>
              <Td pin>
                <Link
                  className="font-mono tabular text-brand hover:underline"
                  to={`/invoices/${r.id}${context}`}
                >
                  {r.invNo}
                </Link>
              </Td>
              <Td className="font-mono tabular text-xs">
                {formatDate(r.date ?? r.createdAt)}
              </Td>
              <Td>
                <div
                  className="w-64"
                  title={[r.billToName, r.billToContact]
                    .filter(Boolean)
                    .join(" · ")}
                >
                  <div className="flex items-start gap-1.5"><CompanyTag region={r.billToRegion} className="mt-0.5" /><div className="min-w-0"><Stack top={r.billToName} bottom={r.billToContact} /></div></div>
                </div>
              </Td>
              <Td align="right" className="font-mono tabular">
                <div>{formatMoney(r.totalAmount, r.currency)}</div>
                <div className="text-2xs text-ink-faint">{r.currency}</div>
              </Td>
              <Td>
                <Link
                  className="font-mono text-xs text-brand hover:underline"
                  to={`/proforma-invoices/${r.piId}`}
                >
                  {r.piNo}
                </Link>
              </Td>
              <Td className="font-mono tabular text-xs">
                {r.customerPoNo || "—"}
              </Td>
              <Td align="right" className="font-mono tabular">
                {r.itemCount}
              </Td>
              {full && (
                <>
                  <Td>
                    <Stack
                      top={r.notifyPartyName}
                      bottom={r.notifyPartyContact}
                    />
                  </Td>
                  <Td>
                    <Stack top={r.consigneeName} bottom={r.consigneeContact} />
                  </Td>
                  <Td className="text-xs">{r.paymentCompany || "—"}</Td>
                  <Td>
                    <Stack
                      monoTop
                      top={formatDate(r.createdAt)}
                      bottom={r.createdByName}
                    />
                  </Td>
                </>
              )}
              <Td pinRight>
                <div className="flex justify-end gap-1">
                  <Link
                    className="rounded-sm p-1 text-xs text-brand hover:bg-sunken"
                    to={`/invoices/${r.id}${context}`}
                  >
                    {t(
                      archived
                        ? "invoiceWorkspace.viewArchived"
                        : "invoiceWorkspace.view",
                    )}
                  </Link>
                  {!archived && canEdit && (
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`${t("common.edit")}: ${r.invNo}`}
                      onClick={() =>
                        navigate(`/invoices/${r.id}/edit${context}`)
                      }
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                  )}
                </div>
              </Td>
            </Tr>
          ))}
        </tbody>
      </ListTableFrame>
    </PageBody>
  );
}
